import { useState, useMemo } from 'react';
import { DateTime } from 'luxon';
import { AlertTriangle, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { toast } from 'sonner';
import { useActiveTopics } from '../../hooks/useExpertTipTopics';
import {
  useExpertTipTopicSchedules,
  useCreateExpertTipTopicSchedule,
  useDeleteExpertTipTopicSchedule,
} from '../../hooks/useExpertTipTopicSchedules';
import { formatWeekLabel, isWeekCurrentOrFuture } from '../../utils/topicScheduleHelpers';
import { getYearMonth } from '../../utils/dateHelpers';

interface Props {
  weekStart: string;
  onClose: () => void;
}

export default function TopicAssignmentModal({ weekStart: initialWeekStart, onClose }: Props) {
  const [currentWeekStart, setCurrentWeekStart] = useState(initialWeekStart);
  const [selectedTopicId, setSelectedTopicId] = useState<number | null>(null);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  const { data: activeTopics = [] } = useActiveTopics();

  const weekDate = DateTime.fromISO(currentWeekStart);
  const { year, month } = getYearMonth(weekDate.toJSDate());
  const { data: scheduleData } = useExpertTipTopicSchedules({ year, month });

  const createMutation = useCreateExpertTipTopicSchedule();
  const deleteMutation = useDeleteExpertTipTopicSchedule();

  const currentSchedule = useMemo(() => {
    return scheduleData?.items.find((s) => s.weekStart === currentWeekStart) ?? null;
  }, [scheduleData, currentWeekStart]);

  const isPast = !isWeekCurrentOrFuture(currentWeekStart);

  const goToPrevWeek = () => {
    const prev = DateTime.fromISO(currentWeekStart).minus({ weeks: 1 });
    setCurrentWeekStart(`${prev.toISODate()}T00:00:00.000Z`);
    setSelectedTopicId(null);
    setConfirmOverwrite(false);
    setConfirmClear(false);
  };

  const goToNextWeek = () => {
    const next = DateTime.fromISO(currentWeekStart).plus({ weeks: 1 });
    setCurrentWeekStart(`${next.toISODate()}T00:00:00.000Z`);
    setSelectedTopicId(null);
    setConfirmOverwrite(false);
    setConfirmClear(false);
  };

  const handleAssign = async () => {
    if (!selectedTopicId) return;

    if (currentSchedule?.source === 'admin' && !confirmOverwrite) {
      setConfirmOverwrite(true);
      return;
    }

    try {
      await createMutation.mutateAsync({
        topicId: selectedTopicId,
        weekStart: currentWeekStart,
      });
      toast.success('Topic scheduled successfully');
      setSelectedTopicId(null);
      setConfirmOverwrite(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to schedule topic');
    }
  };

  const handleClear = async () => {
    if (!currentSchedule) return;

    if (!confirmClear) {
      setConfirmClear(true);
      return;
    }

    try {
      await deleteMutation.mutateAsync(currentSchedule.id.toString());
      toast.success('Schedule cleared');
      setConfirmClear(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to clear schedule');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-500">Assign Topic</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Week Navigation */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
          <button onClick={goToPrevWeek} className="p-1 hover:bg-gray-200 rounded">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="font-medium text-sm text-gray-500">
            {formatWeekLabel(currentWeekStart)}
          </span>
          <button onClick={goToNextWeek} className="p-1 hover:bg-gray-200 rounded">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {/* Current Assignment */}
          {currentSchedule ? (
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Current:</span>
              <span className="font-medium text-sm text-gray-500">{currentSchedule.topicTitle}</span>
              <span
                className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                  currentSchedule.source === 'admin'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {currentSchedule.source}
              </span>
            </div>
          ) : (
            <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-500">
              No topic scheduled — auto-fallback will apply
            </div>
          )}

          {/* Topic Selector (only for current/future weeks) */}
          {!isPast && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Topic
                </label>
                <select
                  value={selectedTopicId ?? ''}
                  onChange={(e) =>
                    setSelectedTopicId(e.target.value ? parseInt(e.target.value, 10) : null)
                  }
                  className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm ${selectedTopicId ? 'text-gray-900' : 'text-gray-600'}`}
                >
                  <option value="">Choose a topic...</option>
                  {activeTopics.map((topic) => (
                    <option
                      key={topic.id}
                      value={topic.id}
                      disabled={topic.publishedTipCount === 0}
                    >
                      {topic.title} ({topic.publishedTipCount} tips)
                      {topic.publishedTipCount === 0 ? ' — no tips' : ''}
                    </option>
                  ))}
                </select>
                {selectedTopicId && (() => {
                  const topic = activeTopics.find((t) => t.id === selectedTopicId);
                  return topic ? (
                    <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                      <span className="text-sm font-medium text-blue-800">{topic.title}</span>
                      <span className="text-xs text-blue-500">{topic.publishedTipCount} tips</span>
                    </div>
                  ) : null;
                })()}
              </div>

              {/* Overwrite confirmation panel */}
              {confirmOverwrite && (
                <div className="flex flex-col gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                    <p className="text-sm text-amber-800">
                      <span className="font-semibold">"{currentSchedule?.topicTitle}"</span> is already scheduled for this week. Replace it with{' '}
                      <span className="font-semibold">"{activeTopics.find((t) => t.id === selectedTopicId)?.title}"</span>?
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfirmOverwrite(false)}
                      className="flex-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAssign}
                      disabled={createMutation.isPending}
                      className="flex-1 px-3 py-1.5 text-sm font-medium text-white bg-amber-500 rounded-lg hover:bg-amber-600 disabled:opacity-50"
                    >
                      {createMutation.isPending ? 'Replacing...' : 'Yes, replace'}
                    </button>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {!confirmOverwrite && (
                <div className="flex gap-2">
                  <button
                    onClick={handleAssign}
                    disabled={!selectedTopicId || createMutation.isPending}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createMutation.isPending ? 'Assigning...' : 'Assign'}
                  </button>

                  {currentSchedule && (
                    <button
                      onClick={handleClear}
                      disabled={deleteMutation.isPending}
                      className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50"
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}

              {/* Clear confirmation panel */}
              {confirmClear && (
                <div className="flex flex-col gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                    <p className="text-sm text-red-800">
                      Remove <span className="font-semibold">"{currentSchedule?.topicTitle}"</span> from this week? Auto-fallback will apply instead.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfirmClear(false)}
                      className="flex-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleClear}
                      disabled={deleteMutation.isPending}
                      className="flex-1 px-3 py-1.5 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50"
                    >
                      {deleteMutation.isPending ? 'Clearing...' : 'Yes, clear'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {isPast && (
            <p className="text-sm text-gray-400 italic">
              Past weeks are read-only
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
